/**
 * IStorageProvider - asset storage abstraction for remote or local retrieval.
 * Handles path-to-URL resolution, file downloads, uploads, and project builds.
 */

export interface FileItem {
  name: string;
  path: string;
  absolutePath: string;
  size: number;
  modifiedTime: Date;
  contentType?: string;
  isDirectory: boolean;
}

export interface FileListResult {
  files: FileItem[];
  directories: FileItem[];
}

export interface FileUploadOptions {
  contentType?: string;
  metadata?: Record<string, string>;
  onProgress?: (progress: number) => void;
  /**
   * Additional options specific to the storage provider
   * e.g. gameId, userId, etc.
   */
  additional?: Record<string, any>;
}

export enum AssetPathEncodeState {
  Unknown = 0,
  Encoded = 1,
  Decoded = 2,
}

/**
 * AssetPath - path representation for engine assets.
 * Handles @engine/ and @project/ prefix resolution to fetch-compatible URLs.
 */
export class AssetPath {
  /**
   * The initial potentially unresolved path, can be one of the following:
   * /foo/bar.txt: path relative to the site root
   * foo/bar.txt: path relative to the current page
   * @engine/foo/bar.txt: path relative to the site root
   * @project/foo/bar.txt: path relative to the project root
   * http://localhost:3000/foo/bar.txt: absolute path
   */
  public readonly initialPath: string;

  /**
   * Resolved fetch compatible path, can be used to call fetch(url) directly
   */
  private resolvedPathEncoded: string | null = null;

  /**
   * Resolved decoded path, only for debugging purposes
   */
  private resolvedPathDecoded: string | null = null;

  constructor(initialPath: string) {
    this.initialPath = initialPath;
  }

  /**
   * Returns {@link initialPath} with backslashes replaced by forward slashes.
   * Use this (not `initialPath`) for prefix validation, storage resolution, and persisted scene paths.
   */
  get asStringPath(): string {
    return AssetPath.withForwardSlashes(this.initialPath);
  }

  /**
   * Creates an AssetPath from a string
   *
   * @param initialPath - The initial path
   * @returns The AssetPath
   */
  public static fromString(initialPath: string): AssetPath {
    return new AssetPath(initialPath);
  }

  /**
   * Joins multiple paths together
   *
   * @param paths - The paths to join
   * @returns The joined path
   */
  public static join(...paths: string[]): string {
    if (paths.length < 2) {
      return this.withForwardSlashes(paths.join('/'));
    }
    // clean up all the path elements so they can be joined cleanly
    paths = paths.map(elem => this.withForwardSlashes(elem));
    const startWithSlash = paths[0].startsWith('/');
    const endWithSlash = paths[paths.length - 1].endsWith('/');
    paths = paths.map(elem => this.removeLeadingSlash(elem));
    paths = paths.map(elem => this.removeTrailingSlash(elem));
    let joinedPath = paths.join('/');
    if (startWithSlash) {
      joinedPath = '/' + joinedPath;
    }
    if (endWithSlash) {
      joinedPath = joinedPath + '/';
    }
    return joinedPath;
  }

  public static normalizeUrl(input: string): { decoded: string; encoded: string } {
    let decoded: string;

    // Try to decode every %XX. If it's not valid percent-encoding, just keep the original.
    try {
      decoded = decodeURIComponent(input);
    } catch {
      decoded = input;
    }

    // Now escape any characters that would break a URL (spaces, unicode, etc.)
    const encoded = encodeURI(decoded);

    return { decoded, encoded };
  }

  /**
   * Converts backslashes to forward slashes
   *
   * @param path - The path to convert
   * @returns The path with backslashes converted to forward slashes
   */
  public static withForwardSlashes(path: string): string {
    return path.replace(/\\/g, '/');
  }

  /**
   * Removes leading slashes from a path
   *
   * @param path - The path to remove leading slashes from
   * @returns The path with leading slashes removed
   */
  public static removeLeadingSlash(path: string): string {
    return path.replace(/^\/+/g, '');
  }

  /**
   * Removes trailing slashes from a path
   *
   * @param path - The path to remove trailing slashes from
   * @returns The path with trailing slashes removed
   */
  public static removeTrailingSlash(path: string): string {
    return path.replace(/\/+$/g, '');
  }

  /**
   * Validates that a path uses the exact prefix followed by '/' or end of string
   *
   * @param path - The path to validate
   * @param expectedPrefix - The expected prefix (e.g., '@project' or '@engine')
   * @throws Error if the path starts with the prefix but doesn't have '/' after it
   */
  public static validatePrefix(path: string, expectedPrefix: string): void {
    if (path.startsWith(expectedPrefix)) {
      const charAfterPrefix = path[expectedPrefix.length];
      if (charAfterPrefix !== '/' && charAfterPrefix !== undefined) {
        throw new Error(
          `Invalid asset path prefix: "${path.split('/')[0]}". ` +
          `Did you mean "${expectedPrefix}/"?`
        );
      }
    }
  }

  /**
   * Validates and strips a prefix from a path
   *
   * @param path - The path to process
   * @param prefix - The prefix to strip (e.g., '@project' or '@engine')
   * @returns The path with the prefix and following '/' removed
   * @throws Error if the path starts with the prefix but doesn't have '/' after it
   */
  public static stripPrefix(path: string, prefix: string): string {
    if (!path.startsWith(prefix)) {
      return path;
    }

    this.validatePrefix(path, prefix);
    return path.slice(prefix.length);
  }

  /**
   * Returns a string representation of the asset path
   */
  public toString(): string {
    return `AssetPath(initial="${this.initialPath}", resolved=${this.resolvedPathEncoded}, decoded=${this.resolvedPathDecoded})`;
  }

  /**
   * Returns true if the path has been resolved
   */
  public isResolved(): boolean {
    return this.resolvedPathEncoded !== null;
  }

  /**
   * Resolves the path to a fetch compatible URL, throws an error if the path is already resolved
   *
   * @param resolvedPath - The resolved path
   * @param encodeState - The encode state of the path
   */
  public resolvePath(resolvedPath: string, encodeState: AssetPathEncodeState): this {
    if (this.resolvedPathEncoded !== null) {
      throw new Error('Path already resolved');
    }

    try {
      const { decoded, encoded } = AssetPath.normalizeUrl(resolvedPath);
      if (encodeState === AssetPathEncodeState.Unknown) {
        this.resolvedPathEncoded = encoded;
        this.resolvedPathDecoded = decoded;
      } else if (encodeState === AssetPathEncodeState.Encoded) {
        this.resolvedPathEncoded = resolvedPath;
        this.resolvedPathDecoded = decoded;
      } else if (encodeState === AssetPathEncodeState.Decoded) {
        this.resolvedPathEncoded = encoded;
        this.resolvedPathDecoded = resolvedPath;
      }
    } catch (error) {
      throw new Error(`Failed to resolve path: ${resolvedPath}`);
    }
    return this;
  }

  /**
   * Returns the resolved path, throws an error if the path is not resolved
   */
  public getResolvedPath(urlEncoded: boolean = true): string {
    const resolvedPath = urlEncoded ? this.resolvedPathEncoded : this.resolvedPathDecoded;
    if (resolvedPath === null) {
      throw new Error(`${this} not resolved`);
    }
    return resolvedPath;
  }
}

export interface IStorageProvider {
  /**
   * Resolves a path to a fetch compatible URL
   *
   * @param path - The path of the file
   * @param expiry - Optional expiry time in milliseconds
   */
  resolvePath(path: AssetPath, expiry?: number): Promise<AssetPath>;

  /**
   * Downloads a file and gets buffer content
   *
   * @param path - The path of the file to download
   */
  downloadFileAsBuffer(
    path: AssetPath,
  ): Promise<ArrayBuffer>;

  /**
   * Downloads a file as a JSON object
   *
   * @param path - The path of the file to download
   */
  downloadFileAsJson<T>(
    path: AssetPath,
  ): Promise<T>;

  /**
   * Downloads a file as a text string
   *
   * @param path - The path of the file to download
   */
  downloadFileAsText(
    path: AssetPath,
  ): Promise<string>;

    /**
   * Uploads a file to the specified path
   *
   * @param path - The destination path including filename
   * @param content - The file content as a Blob, File, string, or ArrayBuffer
   * @param options - Upload options
   */
  uploadFile(
    path: AssetPath,
    content: Blob | File | string | ArrayBuffer,
    options?: FileUploadOptions
  ): Promise<{ path: string; name: string }>;


  /**
   * Lists files and directories at the specified path
   *
   * @param path - The path to list
   * @param recursive - Whether to recursively list subdirectories
   * @param includeHiddenFiles - Whether to include hidden files
   */
  listFiles(path: AssetPath, recursive?: boolean, includeHiddenFiles?: boolean): Promise<FileListResult>;

  /**
   * Checks if a file or directory exists
   *
   * @param path - The path to check
   */
  exists(path: AssetPath): Promise<boolean>;

  /**
   * Builds the current project
   */
  buildCurrentProject(): Promise<boolean>;
}
